// Pure, framework-agnostic helpers for the blog feature.
// Kept dependency-free (no Firebase/React imports) so they're easy to unit test.

// Same slugify algorithm already used for products (src/utils/seedData.js), reused for consistency.
export const slugify = (text) =>
  (text || '')
    .toString()
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/\s+/g, '-')           // Replace spaces with -
    .replace(/[^\w-]+/g, '')        // Remove all non-word chars
    .replace(/--+/g, '-')           // Replace multiple - with single -
    .replace(/^-+/, '')             // Trim - from start of text
    .replace(/-+$/, '');            // Trim - from end of text

export const BLOG_STATUS = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
  SCHEDULED: 'scheduled'
};

// A post is publicly visible only if it's published (or scheduled) AND its publish date has passed.
export const isPubliclyVisible = (post, now = new Date()) => {
  if (!post) return false;
  if (post.status !== BLOG_STATUS.PUBLISHED && post.status !== BLOG_STATUS.SCHEDULED) return false;
  if (!post.publishedAt) return false;
  const publishDate = post.publishedAt?.toDate ? post.publishedAt.toDate() : new Date(post.publishedAt);
  return publishDate.getTime() <= now.getTime();
};

// Strips HTML tags for excerpt/word-count purposes (content is already sanitized before storage/render).
export const stripHtml = (html) => (html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const WORDS_PER_MINUTE = 200;

export const estimateReadingTime = (html) => {
  const text = stripHtml(html);
  const wordCount = text ? text.split(' ').filter(Boolean).length : 0;
  return Math.max(1, Math.round(wordCount / WORDS_PER_MINUTE));
};

// Fields that must be filled in before a post can be published (drafts can stay incomplete).
export const REQUIRED_FOR_PUBLISH = ['title', 'slug', 'content', 'metaDescription'];

export const getPublishValidationErrors = (post) => {
  const errors = {};
  REQUIRED_FOR_PUBLISH.forEach((field) => {
    const value = field === 'content' ? stripHtml(post[field]) : post[field];
    if (!value || !value.toString().trim()) {
      errors[field] = 'Ce champ est requis pour publier un article.';
    }
  });
  return errors;
};

export const isSlugValid = (slug) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug || '');

// Builds the SEO-facing title/description, falling back sensibly when SEO fields are empty.
export const buildSeoMeta = (post, siteName = 'El Hiba Para') => {
  const title = post.metaTitle?.trim() || `${post.title} | ${siteName}`;
  const description = post.metaDescription?.trim() || stripHtml(post.excerpt || post.content).slice(0, 160);
  const image = post.ogImage?.trim() || post.featuredImageUrl || '';
  return { title, description, image };
};

export const formatBlogDate = (value) => {
  const date = value?.toDate ? value.toDate() : (value ? new Date(value) : null);
  if (!date) return '';
  return date.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
};
